/**
 * DeckErrorBoundary — turn a fatal deck-load/render error into a self-healing
 * recovery instead of a blank screen.
 *
 * Why this exists: the deck route reads a lazily-loaded `Deck` through a
 * Suspense resource (`getDeckResource`). Suspense catches thrown *promises*
 * (the pending state) but NOT thrown *errors*. If the deck's code fails to
 * load — most notably on the FIRST authenticated load of a `/private/*` deck,
 * where Cloudflare Access can challenge a subrequest — `resource.read()`
 * throws the error, and with no boundary React unmounts the whole tree, leaving
 * the page blank in the background colour. The rejected resource is cached at
 * module scope, so it stays blank until a full reload resets the module.
 *
 * A full reload is exactly what reliably fixes that transient state (the second
 * load carries an established Access session), so this boundary auto-reloads
 * ONCE per session on the first such error. A `sessionStorage` guard prevents a
 * reload loop; if the error survives the retry we show a graceful "Reload"
 * affordance rather than a blank page. `clearDeckReloadFlag()` is called by the
 * route once a deck has rendered successfully, so a later genuine error in the
 * same session can retry again.
 */
import { Component, type ReactNode } from "react";

export const DECK_RELOAD_FLAG = "deck-load-reloaded-once";

/** Clear the one-shot reload guard. Call after a deck renders successfully. */
export function clearDeckReloadFlag(): void {
  try {
    sessionStorage.removeItem(DECK_RELOAD_FLAG);
  } catch {
    /* sessionStorage unavailable (private mode / SSR) — nothing to clear */
  }
}

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

export class DeckErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    let alreadyRetried = false;
    try {
      alreadyRetried = sessionStorage.getItem(DECK_RELOAD_FLAG) === "1";
    } catch {
      /* sessionStorage unavailable — skip the auto-reload, show the UI */
    }

    if (!alreadyRetried) {
      try {
        sessionStorage.setItem(DECK_RELOAD_FLAG, "1");
      } catch {
        /* ignore */
      }
      // The reload re-fetches with an established session, which clears the
      // transient first-load failure. Guarded above so it happens at most once.
      if (typeof window !== "undefined") {
        window.location.reload();
      }
      return;
    }

    // Retry already used and it still failed — leave a breadcrumb and let
    // render() show the graceful fallback.
    // eslint-disable-next-line no-console
    console.error("[DeckErrorBoundary] deck failed to load after retry:", error);
  }

  private handleReload = (): void => {
    clearDeckReloadFlag();
    if (typeof window !== "undefined") window.location.reload();
  };

  render(): ReactNode {
    if (this.state.failed) {
      let alreadyRetried = false;
      try {
        alreadyRetried = sessionStorage.getItem(DECK_RELOAD_FLAG) === "1";
      } catch {
        /* sessionStorage unavailable */
      }
      // First failure: `componentDidCatch` is about to auto-reload, so render
      // nothing rather than flashing the fallback for a frame before reload.
      if (!alreadyRetried) return null;
      // The auto-reload already happened and it still failed — show a graceful,
      // non-blank recovery affordance instead of an empty screen.
      return (
        <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-cf-bg-100 px-6 text-center">
          <p className="cf-tag">Couldn’t load the deck</p>
          <p className="max-w-md text-sm text-cf-text-muted">
            Something interrupted loading. This is usually transient — reload to
            try again.
          </p>
          <button type="button" className="cf-btn-ghost" onClick={this.handleReload}>
            Reload
          </button>
        </main>
      );
    }
    return this.props.children;
  }
}
